import React, {createContext, useEffect, useState, useContext, useCallback} from 'react';
import {useNavigate, useLocation} from 'react-router-dom';
// import AuthAlertDialog from "Components/Shared/AuthAlertDialog";
import {isTokenValidate, extractTokenDetails} from 'Helpers/ats.helper';
import {publicPaths, Paths} from 'Router/Paths';

const UserContext = createContext();

// User context
const useUserContext = () => {
	return useContext(UserContext);
};

// Context provider wrapper
const UserContextProvider = ({children}) => {
	const [authToken, setAuthToken] = useState('');
	const [loggedIn, setLoggedIn] = useState(false);
	const [userDetails, setuserDetails] = useState(null);
	const [isAuthErr, setAuthErr] = useState(false);
	const [breadCrumb, setBreadCrumb] = useState({});
	const [permission, setPermission] = useState(null);

	const [windowWidth, setWindowWidth] = useState(window.innerWidth);

	const navigate = useNavigate();
	const location = useLocation();
	let authorization;

	/**
	 * Centralized clean logout handler:
	 * Clears all stored auth keys/data, resets user context states,
	 * and safely redirects the user to the login screen.
	 */
	const logout = useCallback(() => {
		localStorage.clear();
		setAuthToken('');
		setuserDetails(null);
		setLoggedIn(false);
		if (window.location.pathname !== Paths.base) {
			navigate(Paths.base, {replace: true});
		}
	}, [navigate]);

	/**
	 * handle role based redirection
	 */
	const roleBasedRedirect = auth => {
		if (isTokenValidate(auth)) {
			// const { role } = extractTokenDetails(auth);
			const role = 'ADMIN';
			switch (role) {
				case 'ADMIN':
					if (location.pathname === '/admin' || location.pathname === '/admin/' || location.pathname === '/')
						if (localStorage.getItem('crmUser') == 'true') {
							navigate(Paths.Crm, {replace: true});
						} else {
							navigate(Paths.users, {replace: true});
						}
					break;
				case 'INVESTOR':
					if (location.pathname === '/admin') navigate('/', {replace: true});
					break;
				case 'DEALER':
					if (location.pathname === '/admin') navigate('/', {replace: true});
					break;
			}
		} else {
			// handled logged out user
			if (
				location.pathname != '/admin' &&
				location.pathname.startsWith('/admin') &&
				!isTokenValidate(authorization)
			) {
				localStorage.removeItem('Authorization');
				authorization = null;
				navigate(Paths.admin);
			}
		}
	};

	/**
	 * handle role based routing
	 */
	useEffect(() => {
		const auth = localStorage.getItem('Authorization');

		// Get new token from URL
		roleBasedRedirect(auth);
	}, [location]);

	useEffect(() => {
		// Get existing token
		const auth = localStorage.getItem('Authorization');

		// Get new token from URL
		const queryString = window.location.search;
		const urlParams = new URLSearchParams(queryString);
		if (auth && auth !== 'null' && isTokenValidate(auth) && !urlParams.get('token')) {
			authorization = auth;
			setLoggedIn(true);
			setuserDetails(extractTokenDetails(auth));
			roleBasedRedirect(auth);
		} else {
			authorization = urlParams.get('token');

			/**
			 * Check if token available and valid
			 */
			const isPublicPath = publicPaths.includes(location.pathname.split('/')[1]);
			if (!isPublicPath) {
				if (!authorization) {
					setAuthErr(true);
					localStorage.removeItem('Authorization');
				} else {
					if (isTokenValidate(authorization)) {
						setAuthToken(authorization);
						localStorage.setItem('Authorization', authorization);
						setuserDetails(extractTokenDetails(authorization));
						setLoggedIn(true);
						window.location.href = '/';
						// roleBasedRedirect(authorization);
					} else {
						setAuthErr(true);
						localStorage.removeItem('Authorization');
						authorization = null;
					}
				}
			}
		}
	}, []);

	/**
	 * Continuous auto-logout watcher:
	 * Monitors token expiration in real-time so the user is automatically logged out
	 * when their JWT expires, without waiting for a manual refresh or an API failure.
	 *
	 * Mechanisms included:
	 * 1. Immediate validation check on active session state change.
	 * 2. Exact setTimeout scheduled for the precise token expiration timestamp.
	 * 3. Periodic interval (every 5 seconds) as fallback against clock drift or throttled timers.
	 * 4. Focus & visibilitychange listeners to catch expiration when computer wakes or tab is revisited.
	 */
	useEffect(() => {
		// Validates whether the current active token has expired
		const checkTokenExpiration = () => {
			const currentToken = localStorage.getItem('Authorization');
			if (!currentToken || currentToken === 'null') {
				if (loggedIn) {
					logout();
				}
				return;
			}

			if (!isTokenValidate(currentToken)) {
				logout();
			}
		};

		const token = localStorage.getItem('Authorization');
		if (!token || token === 'null' || !loggedIn) {
			return;
		}

		// Run immediate check
		checkTokenExpiration();

		const details = extractTokenDetails(token);
		if (!details || !details.exp) {
			logout();
			return;
		}

		const currentTime = Math.floor(Date.now() / 1000);
		const remainingMs = (details.exp - currentTime) * 1000;

		// If token already expired, log out immediately
		if (remainingMs <= 0) {
			logout();
			return;
		}

		// 1. Precise one-shot timer for the exact token expiration moment
		const timerId = setTimeout(() => {
			checkTokenExpiration();
		}, remainingMs);

		// 2. Periodic interval check every 5s (handles system sleep, tab throttling)
		const intervalId = setInterval(() => {
			checkTokenExpiration();
		}, 5000);

		// 3. Tab visibility / window focus check when returning to tab
		const handleVisibilityOrFocus = () => {
			if (document.visibilityState === 'visible') {
				checkTokenExpiration();
			}
		};

		window.addEventListener('focus', handleVisibilityOrFocus);
		document.addEventListener('visibilitychange', handleVisibilityOrFocus);

		return () => {
			clearTimeout(timerId);
			clearInterval(intervalId);
			window.removeEventListener('focus', handleVisibilityOrFocus);
			document.removeEventListener('visibilitychange', handleVisibilityOrFocus);
		};
	}, [loggedIn, authToken, logout]);

	return (
		<UserContext.Provider
			value={{
				userDetails,
				authToken,
				loggedIn,
				windowWidth,
				setWindowWidth,
				setLoggedIn,
				setuserDetails,
				setAuthErr,
				setBreadCrumb,
				breadCrumb,
				permission,
				setPermission,
				logout,
			}}>
			{isAuthErr ? null : null}
			{children}
		</UserContext.Provider>
	);
};

export {useUserContext, UserContextProvider};
